/* eslint-disable @typescript-eslint/no-explicit-any -- Import mapping data is partially dynamic; restrict as part of a focused typing pass. */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Flex,
  SegmentedControl,
  Separator,
  StatusChip,
  Table,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react';
import { FileFormatIcon } from '@/components/ui/FileFormatIcon';
import { CsvFileHoverCard } from '@/components/data-import/CsvFileHoverCard';
import { FormDisplay } from '@/components/data-import/FormDisplay';
import { ImportValidationErrors } from '@/components/data-import/ImportValidationErrors';
import type { CsvFile } from '@/types/csvFiles';
import type { StudyForm } from '@/types/studyForms';

interface FileMappingError {
  row?: number;
  column?: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface FileMappingDetail {
  file_name: string;
  form_name: string;
  csv_file_id?: number;
  study_form_id?: number;
  match_type?: string;
  status?: 'success' | 'failed' | 'partial' | 'pending';
  records_processed?: number;
  records_total?: number;
  records_success?: number;
  records_failed?: number;
  column_mappings?: Array<{
    column_name: string;
    mapping_type: 'metadata' | 'formField' | 'ignored';
    metadata_type?: string | null;
    target_field?: string;
  }>;
  errors?: FileMappingError[];
  file_error?: string; // Error message if entire file failed
}

interface ImportFileMappingsProps {
  fileMappings: FileMappingDetail[];
  csvFiles?: Map<number, CsvFile>;
  studyForms?: Map<number, StudyForm>;
  importValidationErrors?: {
    [studyFormId: string]: string | { forms?: Record<string, string>; files?: Record<string, string> } | Record<string, unknown>;
  } | {
    forms?: Record<string, string>;
    files?: Record<string, string>;
  } | Record<string, unknown>;
}

// Mock functions removed - using only real data from API

export const ImportFileMappings: React.FC<ImportFileMappingsProps> = ({
  fileMappings,
  csvFiles = new Map(),
  studyForms = new Map(),
  importValidationErrors,
}) => {
  const [expandedMappings, setExpandedMappings] = useState<Set<number>>(
    new Set()
  );
  const [viewMode, setViewMode] = useState<'mappings' | 'errors'>('mappings');
  const hasMappings = fileMappings.length > 0;

  const toggleExpanded = (index: number) => {
    setExpandedMappings(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Get validation errors for each mapping's form and file
  const getFormValidationErrors = useMemo(() => {
    const formErrorsMap = new Map<number, string>();
    const fileErrorsMap = new Map<number, string>();
    
    if (importValidationErrors && typeof importValidationErrors === 'object') {
      // Check if errors has the structure { forms: {...}, files: {...} }
      if ('forms' in importValidationErrors || 'files' in importValidationErrors) {
        const errorObj = importValidationErrors as { forms?: Record<string, string>; files?: Record<string, string> };
        
        // Extract form errors from forms object
        if (errorObj.forms && typeof errorObj.forms === 'object') {
          Object.entries(errorObj.forms).forEach(([formIdStr, message]) => {
            const formId = parseInt(formIdStr, 10);
            if (!isNaN(formId)) {
              formErrorsMap.set(formId, typeof message === 'string' ? message : JSON.stringify(message));
            }
          });
        }
        
        // Extract file errors from files object
        if (errorObj.files && typeof errorObj.files === 'object') {
          Object.entries(errorObj.files).forEach(([fileIdStr, message]) => {
            const fileId = parseInt(fileIdStr, 10);
            if (!isNaN(fileId)) {
              fileErrorsMap.set(fileId, typeof message === 'string' ? message : JSON.stringify(message));
            }
          });
        }
      } else {
        // Normal case: errors is already { [formId]: message }
        Object.entries(importValidationErrors).forEach(([formIdStr, message]) => {
          const formId = parseInt(formIdStr, 10);
          if (!isNaN(formId)) {
            formErrorsMap.set(formId, typeof message === 'string' ? message : JSON.stringify(message));
          }
        });
      }
    }
    
    return { formErrorsMap, fileErrorsMap };
  }, [importValidationErrors]);

  // Use real data only - no mock generation
  const enhancedMappings = useMemo(() => {
    return fileMappings.map(mapping => {
      // Check if this mapping's form has validation errors
      const formValidationError = mapping.study_form_id
        ? getFormValidationErrors.formErrorsMap.get(mapping.study_form_id)
        : undefined;
      
      // Check if this mapping's file has validation errors
      const fileValidationError = mapping.csv_file_id
        ? getFormValidationErrors.fileErrorsMap.get(mapping.csv_file_id)
        : undefined;

      // Use only real data from mapping
      // Determine status based on real errors if available
      let status = mapping.status;
      if (!status) {
        if (mapping.file_error) {
          status = 'failed';
        } else if (mapping.errors && mapping.errors.length > 0) {
          status = 'partial';
        } else if (formValidationError) {
          status = 'failed'; // Form validation error makes mapping invalid
        } else {
          status = 'pending'; // Default to pending if no status provided
        }
      }

      return {
        ...mapping,
        status,
        formValidationError, // Add form validation error to mapping
        fileValidationError, // Add file validation error to mapping
        // Use only real errors and file_error if provided
        errors: mapping.errors || [],
        file_error: mapping.file_error || fileValidationError, // Use fileValidationError if file_error is not set
        // Use only real statistics if provided
        records_total: mapping.records_total,
        records_processed: mapping.records_processed,
        records_success: mapping.records_success,
        records_failed: mapping.records_failed,
      };
    });
  }, [fileMappings, getFormValidationErrors]);

  // Count validation errors (forms + files)
  const validationErrorCount = useMemo(() => {
    if (!importValidationErrors) return 0;
    
    if (typeof importValidationErrors === 'object' && ('forms' in importValidationErrors || 'files' in importValidationErrors)) {
      const errorObj = importValidationErrors as { forms?: Record<string, string>; files?: Record<string, string> };
      const formsCount = errorObj.forms ? Object.keys(errorObj.forms).length : 0;
      const filesCount = errorObj.files ? Object.keys(errorObj.files).length : 0;
      return formsCount + filesCount;
    }
    
    return Object.keys(importValidationErrors).length;
  }, [importValidationErrors]);

  // Calculate error count for each mapping
  const mappingsWithErrorCount = useMemo(() => {
    return enhancedMappings.map(mapping => {
      let errorCount = 0;

      // Count form validation error
      if ((mapping as any).formValidationError) {
        errorCount += 1;
      }

      // Count file validation errors
      if ((mapping as any).fileValidationError) {
        errorCount += 1;
      }

      // Count file errors (legacy)
      if (mapping.file_error && !(mapping as any).fileValidationError) {
        errorCount += 1;
      }

      // Count row-level errors
      if (mapping.errors && mapping.errors.length > 0) {
        errorCount += mapping.errors.length;
      }

      return {
        ...mapping,
        errorCount,
      };
    });
  }, [enhancedMappings]);

  // Filter mappings to show only those with validation errors when in errors view
  const filteredMappings = useMemo(() => {
    if (viewMode === 'errors') {
      // Show mappings that have any errors (form validation, file errors, or row errors)
      return mappingsWithErrorCount.filter(mapping => {
        // Check if form has validation error
        const hasFormValidationError = !!(mapping as any).formValidationError;
        // Check if file has validation error
        const hasFileValidationError = !!(mapping as any).fileValidationError;
        // Check if mapping has file or row errors
        const errorCountFromValidation = (hasFormValidationError ? 1 : 0) + (hasFileValidationError ? 1 : 0);
        const hasOtherErrors = mapping.errorCount > errorCountFromValidation;
        return hasFormValidationError || hasFileValidationError || hasOtherErrors;
      });
    }
    return mappingsWithErrorCount;
  }, [mappingsWithErrorCount, viewMode]);

  if (!hasMappings) {
    return null;
  }

  return (
    <Flex mt="4" direction="column" gap="2">
      <Flex justify="between" align="center" mb="3" gap="3">
        <Text size="3" weight="medium">
          File Mappings
        </Text>

        {/* View mode switcher - show only if validation errors exist */}
        {validationErrorCount > 0 && (
          <SegmentedControl
            items={[
              { value: 'mappings', label: 'Mappings' },
              {
                value: 'errors',
                label: (
                  <Flex align="center" gap="1">
                    <Text size="inherit">Validation Warnings</Text>
                    <Badge size="xsmall" color="error" variant="soft">
                      {validationErrorCount}
                    </Badge>
                  </Flex>
                ),
              },
            ]}
            value={viewMode}
            onValueChange={(value: string) =>
              setViewMode(value as 'mappings' | 'errors')
            }
            size="small"
          />
        )}

        <Badge color="info" variant="soft">
          {enhancedMappings.length} mapping
          {enhancedMappings.length !== 1 ? 's' : ''}
        </Badge>
      </Flex>

      {/* Show validation errors panel when in errors view */}
      {viewMode === 'errors' && importValidationErrors && (
        <ImportValidationErrors
          errors={importValidationErrors}
          studyForms={studyForms}
          csvFiles={csvFiles}
        />
      )}

      {viewMode === 'errors' &&
        filteredMappings.length === 0 &&
        !importValidationErrors && (
          <Card>
            <Flex direction="column" align="center" gap="2" p="4">
              <IconCheck size={24} color="var(--green-9)" />
              <Text size="3" weight="medium">
                No Validation Warnings in Mappings
              </Text>
              <Text size="2" color="gray">
                All mapped forms are valid
              </Text>
            </Flex>
          </Card>
        )}

      {viewMode === 'mappings' && filteredMappings.length > 0 && (
        <Table.Root
          columns="auto 1fr auto 1fr auto auto auto auto"
          variant="surface"
          className="file-mappings-table"
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>#</Table.HeaderCell>
              <Table.HeaderCell>File</Table.HeaderCell>
              <Table.HeaderCell> </Table.HeaderCell>
              <Table.HeaderCell>Form</Table.HeaderCell>
              <VisuallyHidden>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </VisuallyHidden>
              <Table.HeaderCell>Records</Table.HeaderCell>
              <Table.HeaderCell>Errors</Table.HeaderCell>
              <Table.HeaderCell> </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredMappings.map((mapping, filteredIndex) => {
              // Find original index in mappingsWithErrorCount for proper expansion tracking
              const originalIndex = mappingsWithErrorCount.findIndex(
                m =>
                  m.csv_file_id === mapping.csv_file_id &&
                  m.study_form_id === mapping.study_form_id
              );
              const isExpanded =
                originalIndex >= 0 && expandedMappings.has(originalIndex);
              const hasErrors = mapping.errors && mapping.errors.length > 0;
              const hasFileError = !!(mapping.file_error || (mapping as any).fileValidationError);
              const hasFormValidationError = !!(mapping as any)
                .formValidationError;
              const hasFileValidationError = !!(mapping as any).fileValidationError;
              const showExpandButton =
                hasErrors ||
                hasFileError ||
                hasFileValidationError ||
                mapping.column_mappings ||
                hasFormValidationError;

              const csvFile = mapping.csv_file_id
                ? csvFiles.get(mapping.csv_file_id)
                : undefined;
              const studyForm = mapping.study_form_id
                ? studyForms.get(mapping.study_form_id)
                : undefined;

              return (
                <React.Fragment
                  key={originalIndex >= 0 ? originalIndex : filteredIndex}
                >
                  <Table.Row>
                    <Table.Cell>
                      <Text size="2" color="gray">
                        {(originalIndex >= 0 ? originalIndex : filteredIndex) +
                          1}
                      </Text>
                    </Table.Cell>

                    {/* File */}
                    <Table.Cell>
                      {csvFile ? (
                        <CsvFileHoverCard
                          file={csvFile}
                          columns={mapping.column_mappings?.length}
                          rows={mapping.records_total}
                        >
                          <Flex
                            align="center"
                            gap="2"
                            style={{ cursor: 'pointer' }}
                          >
                            <FileFormatIcon
                              fileName={mapping.file_name}
                              mimeType="text/csv"
                              size="xsmall"
                            />
                            <Text
                              size="2"
                              weight="medium"
                              style={{
                                fontFamily: 'var(--font-mono)',
                              }}
                              title={mapping.file_name}
                            >
                              {mapping.file_name}
                            </Text>
                          </Flex>
                        </CsvFileHoverCard>
                      ) : (
                        <Flex align="center" gap="2" width="100%">
                          <FileFormatIcon
                            fileName={mapping.file_name}
                            mimeType="text/csv"
                            size="xsmall"
                          />
                          <Text
                            size="2"
                            weight="medium"
                            style={{
                              fontFamily: 'var(--font-mono)',
                            }}
                            title={mapping.file_name}
                          >
                            {mapping.file_name}
                          </Text>
                          {mapping.file_name.startsWith('CSV File #') && (
                            <Badge
                              color="default"
                              variant="outlined"
                              size="xsmall"
                            >
                              not found
                            </Badge>
                          )}
                        </Flex>
                      )}
                    </Table.Cell>

                    {/* Arrow */}
                    <Table.Cell>
                      <IconArrowRight style={{ color: 'var(--gray-9)' }} />
                    </Table.Cell>

                    {/* Form */}
                    <Table.Cell>
                      {studyForm ? (
                        <Flex align="center" gap="2" width="100%">
                          <FormDisplay
                            form={studyForm}
                            allForms={studyForms}
                            showIcon={true}
                            showBadge={false}
                            size="medium"
                            variant="default"
                          />
                          {/* Show validation error badge if form has error */}
                          {(mapping as any).formValidationError && (
                            <Badge
                              color="error"
                              variant="soft"
                              size="xsmall"
                              title={(mapping as any).formValidationError}
                            >
                              <IconAlertTriangle />
                            </Badge>
                          )}
                        </Flex>
                      ) : (
                        <Flex align="center" gap="2">
                          <Text
                            size="2"
                            weight="medium"
                            title={mapping.form_name}
                          >
                            {mapping.form_name}
                          </Text>
                          {/* Show validation error badge if form has error */}
                          {(mapping as any).formValidationError && (
                            <Badge
                              color="error"
                              variant="soft"
                              size="xsmall"
                              title={(mapping as any).formValidationError}
                            >
                              <IconAlertTriangle />
                            </Badge>
                          )}
                        </Flex>
                      )}
                    </Table.Cell>

                    {/* Status */}
                    <VisuallyHidden>
                      <Table.Cell>
                        <StatusChip
                          entityType="import"
                          status={
                            mapping.status === 'success'
                              ? 'completed'
                              : mapping.status === 'partial'
                                ? 'processing'
                                : mapping.status || 'pending'
                          }
                          size="small"
                        />
                      </Table.Cell>
                    </VisuallyHidden>

                    {/* Records */}
                    <Table.Cell>
                      {mapping.records_total !== undefined ? (
                        <Text size="2" color="gray">
                          {mapping.records_success || 0} /{' '}
                          {mapping.records_total}
                        </Text>
                      ) : (
                        <Text size="2" color="gray">
                          -
                        </Text>
                      )}
                    </Table.Cell>

                    {/* Errors */}
                    <Table.Cell>
                      {mapping.errorCount > 0 ? (
                        <Badge
                          color="error"
                          variant="soft"
                          size="small"
                          title={`${mapping.errorCount} error${mapping.errorCount !== 1 ? 's' : ''}`}
                        >
                          {mapping.errorCount}
                        </Badge>
                      ) : (
                        <Badge color="success" variant="soft" size="small">
                          ✓
                        </Badge>
                      )}
                    </Table.Cell>

                    {/* Actions */}
                    <Table.Cell>
                      {showExpandButton && (
                        <Button
                          variant="ghost"
                          size="small"
                          isIconOnly
                          onClick={() => toggleExpanded(originalIndex)}
                        >
                          {isExpanded ? <IconChevronUp /> : <IconChevronDown />}
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>

                  {/* Expanded details row */}
                  {isExpanded && (
                    <Table.Row>
                      <Table.Cell
                        style={{ display: 'grid', gridColumn: '2 / 7' }}
                      >
                        <Flex direction="column" gap="3">
                          {/* Errors section header - show only if there are errors */}
                          {(hasFormValidationError ||
                            hasFileError ||
                            hasErrors) && (
                            <Flex align="center" gap="2" mb="1">
                              <IconAlertTriangle
                                size={18}
                                color="var(--red-9)"
                              />
                              <Text size="3" weight="medium">
                                Errors ({mapping.errorCount})
                              </Text>
                            </Flex>
                          )}

                          {/* Form validation error */}
                          {hasFormValidationError && (
                            <Alert color="error" title="Form Validation Error">
                              <Flex direction="column" gap="2">
                                <Flex align="center" gap="2">
                                  <IconAlertTriangle color="var(--red-9)" />
                                  <Text size="2" weight="medium">
                                    Form:{' '}
                                    {studyForm
                                      ? studyForm.name
                                      : `Form #${mapping.study_form_id}`}
                                  </Text>
                                </Flex>
                                <Text size="2" style={{ marginLeft: '24px' }}>
                                  {(mapping as any).formValidationError}
                                </Text>
                              </Flex>
                            </Alert>
                          )}

                          {/* File-level error - show only if different from form error */}
                          {hasFileError && 
                           (!hasFormValidationError || 
                            (mapping.file_error || (mapping as any).fileValidationError) !== (mapping as any).formValidationError) && (
                            <Alert color="error" title="File Validation Error">
                              <Flex direction="column" gap="2">
                                <Flex align="center" gap="2">
                                  <IconAlertTriangle color="var(--red-9)" />
                                  <Text size="2" weight="medium">
                                    File:{' '}
                                    {csvFile
                                      ? csvFile.filename
                                      : mapping.file_name}
                                  </Text>
                                </Flex>
                                <Text size="2" style={{ marginLeft: '24px' }}>
                                  {(mapping as any).fileValidationError || mapping.file_error}
                                </Text>
                              </Flex>
                            </Alert>
                          )}

                          {/* Column mappings */}
                          {mapping.column_mappings &&
                            mapping.column_mappings.length > 0 && (
                              <Flex direction="column" gap="2" my="4">
                                <Text size="2" weight="medium">
                                  Column Mappings (
                                  {mapping.column_mappings.length})
                                </Text>
                                <Flex direction="column" gap="1">
                                  {mapping.column_mappings.map(
                                    (cm, cmIndex) => (
                                      <Flex
                                        key={cmIndex}
                                        justify="between"
                                        align="center"
                                        p="2"
                                        style={{
                                          background: 'var(--gray-a2)',
                                          borderRadius: '4px',
                                        }}
                                      >
                                        <Flex
                                          align="center"
                                          gap="2"
                                          style={{
                                            display: 'grid',
                                            width: '100%',
                                            maxWidth: '800px',
                                            gridTemplateColumns:
                                              '1fr 46px 1fr auto auto',
                                          }}
                                        >
                                          <Text
                                            size="1"
                                            style={{
                                              fontFamily: 'var(--font-mono)',
                                              minWidth: '150px',
                                            }}
                                          >
                                            {cm.column_name}
                                          </Text>
                                          <IconArrowRight />
                                          <Text size="1">
                                            {cm.mapping_type === 'metadata'
                                              ? cm.metadata_type || 'Metadata'
                                              : cm.mapping_type === 'formField'
                                                ? cm.target_field ||
                                                  'Form Field'
                                                : 'Ignored'}
                                          </Text>
                                          <Badge
                                            color={
                                              cm.mapping_type === 'metadata'
                                                ? 'info'
                                                : cm.mapping_type ===
                                                    'formField'
                                                  ? 'success'
                                                  : 'gray'
                                            }
                                            variant="soft"
                                            size="small"
                                          >
                                            {cm.mapping_type}
                                          </Badge>
                                        </Flex>
                                      </Flex>
                                    )
                                  )}
                                </Flex>
                              </Flex>
                            )}

                          {/* Row-level errors */}
                          {hasErrors && (
                            <Flex direction="column" gap="2">
                              <Flex align="center" gap="2">
                                <IconAlertTriangle
                                  size={18}
                                  color="var(--orange-9)"
                                />
                                <Text size="2" weight="medium">
                                  Row-level Errors ({mapping.errors!.length})
                                </Text>
                              </Flex>
                              <Flex direction="column" gap="2">
                                {mapping.errors!.map((error, errorIndex) => (
                                  <Alert
                                    key={errorIndex}
                                    color={
                                      error.severity === 'error'
                                        ? 'error'
                                        : 'warning'
                                    }
                                    title={
                                      <Flex align="center" gap="2">
                                        <IconAlertTriangle
                                          color={
                                            error.severity === 'error'
                                              ? 'var(--red-9)'
                                              : 'var(--orange-9)'
                                          }
                                        />
                                        <Text size="2" weight="medium">
                                          {error.row
                                            ? `Row ${error.row}${error.column ? `, Column "${error.column}"` : ''}`
                                            : error.column
                                              ? `Column "${error.column}"`
                                              : 'Error'}
                                        </Text>
                                      </Flex>
                                    }
                                  >
                                    <Text
                                      size="2"
                                      style={{ marginLeft: '24px' }}
                                    >
                                      {error.message}
                                    </Text>
                                  </Alert>
                                ))}
                              </Flex>
                            </Flex>
                          )}

                          {/* Statistics summary */}
                          {mapping.records_total !== undefined && (
                            <>
                              <Separator />
                              <Flex justify="between" align="center">
                                <Flex direction="column" gap="1">
                                  <Text size="1" color="gray">
                                    Success Rate
                                  </Text>
                                  <Text size="3" weight="medium">
                                    {mapping.records_total > 0
                                      ? Math.round(
                                          ((mapping.records_success || 0) /
                                            mapping.records_total) *
                                            100
                                        )
                                      : 0}
                                    %
                                  </Text>
                                </Flex>
                                <Flex direction="column" gap="1" align="end">
                                  <Text size="1" color="gray">
                                    Failed Records
                                  </Text>
                                  <Text size="3" weight="medium" color="error">
                                    {mapping.records_failed || 0}
                                  </Text>
                                </Flex>
                                {mapping.match_type && (
                                  <Flex direction="column" gap="1" align="end">
                                    <Text size="1" color="gray">
                                      Match Type
                                    </Text>
                                    <Badge
                                      color="info"
                                      variant="soft"
                                      size="small"
                                    >
                                      {mapping.match_type}
                                    </Badge>
                                  </Flex>
                                )}
                              </Flex>
                            </>
                          )}
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table.Root>
      )}
    </Flex>
  );
};
